import type { DocsNavGroup, DocsNavSection } from 'BomdisoftComponents/docs-shell/types';
import { nullable, persisted, text } from '@bomdisoft/store/persisted';

export const isSectionNav = (nav: DocsNavGroup[] | DocsNavSection[]): nav is DocsNavSection[] => nav.length > 0 && 'groups' in nav[0];

/** The section holding the active page, else the first: a deep link lands with
 * the right segment selected, and an unknown path degrades to the front. */
export const activeSectionId = (nav: DocsNavSection[], pathname: string, langPrefix: string): string => owningSectionId(nav, pathname, langPrefix) ?? nav[0].id;

/** Where a segment click lands: the section page the reader last visited when
 * the stored path still names one of the section's own items, else the
 * section's first item. Membership is the validation, so a stale entry
 * (another language, a removed page, storage junk) degrades to the section's
 * front rather than navigating nowhere. */
export const sectionTarget = (section: DocsNavSection, stored: string | null, langPrefix: string): string | null => {
  const hrefs = section.groups.flatMap(group => group.items.map(item => item.href(langPrefix)));
  if (hrefs.length === 0) return null;
  const normalized = stored?.replace(/\/+$/, '');
  return hrefs.find(href => href.replace(/\/+$/, '') === normalized) ?? hrefs[0];
};

/** The section whose items include the pathname, or nothing: unlike
 * `activeSectionId` this never falls back to the front, so a visit only counts
 * as being "in" a section when the page really belongs to it. */
export const owningSectionId = (nav: DocsNavSection[], pathname: string, langPrefix: string): string | undefined => {
  const normalized = pathname.replace(/\/+$/, '');
  return nav.find(section => section.groups.some(group => group.items.some(item => item.href(langPrefix).replace(/\/+$/, '') === normalized)))?.id;
};

/** The groups a consumer should treat as "the rail right now": a manual pages
 * and lists within itself, so the pager never walks across section borders. */
export const sectionGroups = (nav: DocsNavGroup[] | DocsNavSection[], pathname: string, langPrefix: string): DocsNavGroup[] => {
  if (!isSectionNav(nav)) return nav;
  const activeId = activeSectionId(nav, pathname, langPrefix);
  return (nav.find(section => section.id === activeId) ?? nav[0]).groups;
};

/** The page each docs section was last read at, per section, so reopening a
 *  section returns the reader to where they were rather than its first page.
 *  Storage can be unavailable in a private window; the tab then opens on its
 *  first page, which is the declared fallback, not an error. */
export const docsLastPage = persisted<string | null>({
  codec: nullable(text()),
  default: null,
  in: ['local'],
  key: 'bomdisoft-docs-last-page',
});
